<script setup>
import { userStore } from '@/stores/user'
import { RouterView, useRouter } from 'vue-router'
import { ref, computed, onMounted } from 'vue';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import ButtonGroup from 'primevue/buttongroup';
import Card from 'primevue/card';
import Divider from 'primevue/divider';
import Message from 'primevue/message';
import { useConfirm } from "primevue/useconfirm";
import { useToast } from 'primevue/usetoast';
import Knob from 'primevue/knob';
import { fetchFromApi } from '@/fetch.ts';

const user = userStore();
const router = useRouter();
const toast = useToast();

const CREDIT_USAGE_WARNING_THRESHOLD = 75; // percentage
const CREDIT_USAGE_DANGER_THRESHOLD = 90; // percentage

const props = defineProps({
    subscription: {
        type: Object,
        required: true
    }
});
const subscription = ref(props.subscription);

const formatLocalTimestamp = (utcTimestamp) => {
  const date = new Date(utcTimestamp);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const apiKey = ref('')
onMounted(() => {
    apiKey.value = subscription.value.api_key;
});

const planName = computed(() => {
    return subscription.value.plan_details.name === 'Starter' ? 'Free' : subscription.value.plan_details.name;
});

const reqTotal = computed(() => {
    return subscription.value.usage.req_used + subscription.value.usage.req_remaining;
});

const reqUsedPercent = computed(() => {
    return (subscription.value.usage.req_used * 100 / reqTotal.value).toFixed(0);
});

const usageBarColor = computed(() => {
    const percent = Number(reqUsedPercent.value);
    if (percent >= CREDIT_USAGE_DANGER_THRESHOLD) return '#ef4444';
    if (percent >= CREDIT_USAGE_WARNING_THRESHOLD) return '#f59e0b';
    return '#22c55e';
});

const isHighUsage = computed(() => {
    return Number(reqUsedPercent.value) >= CREDIT_USAGE_DANGER_THRESHOLD;
});

const isOverLimit = computed(() => {
    return subscription.value.usage.req_remaining <= 0;
});

const confirm = useConfirm();
const confirmRegenerate = () => {
    confirm.require({
        message: 'Use this if your API key has been made public. Existing implementations will need to be updated to use the new API key. This will not reset usage.',
        header: 'Generate a new API key',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Regenerate',
        },
        accept: () => {
            regenerateApiKey();
        },
        reject: () => {
            return;
        }
    });
};


const regenerateLoading = ref(false);
const regenerateError = ref(null);
async function regenerateApiKey() {
    regenerateLoading.value = true;
    regenerateError.value = null;
    try {
        console.log(subscription.value.id)
        const response = await fetchFromApi('/subscriptions/' + subscription.value.id + '/refresh-api-key', 'POST');
        apiKey.value = response.data.api_key;
    } catch (error) {
        regenerateError.value = error;
        console.error('Error regenerating API key:', error);
    } finally {
        regenerateLoading.value = false;
    }
}

const redirecting = ref(false);
const redirectError = ref(null);
async function manageBilling() {
    redirecting.value = true;    
    redirectError.value = null;

    try {
        const response = await fetchFromApi(`/subscriptions/${subscription.value.id}/create-customer-portal-session?redirect_to_dash=true`, 'POST'); 
        if (response.error) {
            redirectError.value = 'Error redirecting to billing: ' + response.error;
            console.error('Error redirecting to billing:', response.error);
            return;
        }
        window.location.href = response.data.url;
    } catch (error) {
        redirectError.value = 'Something went wrong';
        console.error('Error redirecting to billing:', error);
        return;
    } finally {
        redirecting.value = false;
    }
}

function viewInvoice(url) {
    window.location.href = url;
}

function copyApiKey(key) {
    navigator.clipboard.writeText(key).then(() => {
        toast.add({ severity: 'success', summary: 'Copied', detail: 'API key copied to clipboard', life: 3000 });
    }).catch(err => {
        console.error('Failed to copy API key: ', err);
    });
}

</script>

<template>
    <Card class="mx-auto my-5 subscription-card shadow-sm border border-slate-200 rounded-xl" style="max-width: 800px;">
        <template #title>
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <span class="text-lg font-semibold">{{ planName }}</span>
                <Badge severity="success" value="Active" v-if="subscription.status === 'active'" />
                <Badge severity="secondary" value="Paused" v-else-if="subscription.status === 'paused'" />
                <Badge severity="warn" value="Due to Cancel" v-else-if="subscription.status === 'due_to_cancel'" />
                <Badge severity="danger" value="Payment Failed" v-else-if="subscription.status === 'payment_failed'" />
                <Badge severity="secondary" value="Cancelled" v-else-if="subscription.status === 'cancelled'" />
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-6">
                <Message severity="warn" v-if="subscription.status === 'due_to_cancel'" :closable="false">
                    <div v-if="subscription.cancel_at_timestamp_utc">
                        Subscription is due to cancel on {{ formatLocalTimestamp(subscription.cancel_at_timestamp_utc) }}
                    </div>
                    <div v-else>
                        Subscription is due to cancel before the next billing cycle.
                    </div>
                </Message>
                <Message severity="error" v-else-if="subscription.status === 'payment_failed'" :closable="false">
                    The last payment attempt was unsuccessful.
                    <br />
                    <div v-if="subscription.payment_failed_user_facing_reason">
                        <code class="block text-sm p-2">{{ subscription.payment_failed_user_facing_reason }}</code>
                    </div>
                    <div v-if="subscription.next_payment_attempt_timestamp_utc">
                        Next payment attempt: {{ formatLocalTimestamp(subscription.next_payment_attempt_timestamp_utc) }}
                    </div>
                    <div v-if="subscription.hosted_invoice_url">
                        <span class="underline cursor-pointer" @click="viewInvoice(subscription.hosted_invoice_url)">View invoice</span> to update billing information and retry payment.
                    </div>
                    <div v-else>
                        To update billing details, <span class="underline cursor-pointer" @click="manageBilling()">Manage Subscription</span>
                    </div>
                </Message>
                <Message variant="simple" size="small" severity="secondary" v-else-if="subscription.status === 'cancelled'" :closable="false">
                    Subscription has ended. To regain the same level of access, a <a @click="$emit('showNewSubscription')" class="underline cursor-pointer" >new subscription</a> is needed.
                </Message>

                <!-- API key -->
                <section>
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-key"></i>
                        <span>API Key</span>
                    </div>
                    <div class="flex flex-row items-center gap-2">
                        <code class="relative flex-1 min-w-0 py-2 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm">
                            <span class="block overflow-x-auto whitespace-nowrap pl-3 pr-3">
                                {{ apiKey }}
                            </span>
                        </code>
                        <ButtonGroup class="shrink-0">
                            <Button
                                v-if="subscription.api_key_enabled === true"
                                severity="secondary"
                                icon="pi pi-copy"
                                @click="copyApiKey(apiKey)"
                                label="Copy"
                                outlined
                                size="small"
                            />
                            <Button
                                v-if="subscription.api_key_enabled === true"
                                severity="secondary"
                                :loading="regenerateLoading"
                                icon="pi pi-refresh"
                                @click="confirmRegenerate()"
                                label=""
                                v-tooltip="'Regenerate API Key'"
                                outlined
                                size="small"
                            />
                        </ButtonGroup>
                    </div>
                    <Message severity="error" v-if="regenerateError" class="mt-2">
                        {{ regenerateError || 'An error occurred while regenerating the API key.' }}
                    </Message>
                </section>

                <!-- Usage -->
                <section v-if="subscription.status !== 'cancelled'">
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-chart-bar"></i>
                        <span>Usage this period</span>
                    </div>
                    <div class="flex items-center gap-4">
                        <Knob
                            :modelValue="Number(reqUsedPercent)"
                            readonly
                            :size="70"
                            valueTemplate="{value}%"
                            :valueColor="usageBarColor"
                            rangeColor="#e2e8f0"
                        />
                        <div>
                            <span class="text-md font-bold tabular-nums">
                                {{ subscription.usage.req_used.toLocaleString() }}
                                <span class="text-sm text-slate-500">used of</span>
                                {{ reqTotal.toLocaleString() }} credits
                            </span>
                            <p class="text-xs text-slate-400 mt-1">
                                Monthly plans reset on the 1st of each month at 12AM UTC
                            </p>
                        </div>
                    </div>
                    <Message severity="warn" :closable="false" size="small" class="mt-3" v-if="isHighUsage">
                        <template v-if="isOverLimit">Credit limit reached.</template>
                        <template v-else>You're approaching the monthly credit limit.</template>
                        If you need more credits this period, <a href="https://the-odds-api.com/manage/upgrade-downgrade-cancel-a-subscription.html" target="_blank" rel="noopener" class="text-sm underline cursor-pointer font-medium">upgrades are prorated</a>.
                    </Message>
                </section>

                <!-- Plan details -->
                <section>
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <i class="pi pi-info-circle"></i>
                        <span>Plan Details</span>
                    </div>
                    <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                        <dt class="text-slate-400">Price</dt>
                        <dd v-if="Number(subscription.plan_details.price) === 0">Free</dd>
                        <dd v-else>${{ subscription.plan_details.price }} / month</dd>

                        <dt class="text-slate-400">Plan includes</dt>
                        <dd>{{ subscription.plan_details.feature }}</dd>

                        <dt class="text-slate-400">Started</dt>
                        <dd>{{ formatLocalTimestamp(subscription.signup_timestamp_utc) }}</dd>

                        <template v-if="subscription.status === 'cancelled'">
                            <dt class="text-slate-400">Ended</dt>
                            <dd>{{ formatLocalTimestamp(subscription.cancellation_timestamp_utc) }}</dd>
                        </template>

                        <template v-if="subscription.status == 'active' && subscription.next_invoice_timestamp_utc">
                            <dt class="text-slate-400">Next invoice</dt>
                            <dd>{{ formatLocalTimestamp(subscription.next_invoice_timestamp_utc) }}</dd>
                        </template>
                    </dl>
                </section>

                <!-- Billing -->
                <section v-if="subscription.plan_details.is_free === false">
                    <Divider class="!my-0" />

                    <div class="flex items-center justify-between flex-wrap gap-2 pt-4">
                        <Button
                            icon="pi pi-credit-card"
                            v-if="subscription.status !== 'cancelled'"
                            severity="secondary"
                            @click="manageBilling()"
                            label="Manage Subscription"
                            :loading="redirecting"
                            class="border-black"
                            size="small"
                        />
                        <Button
                            v-if="subscription.status === 'cancelled'"
                            icon="pi pi-file"
                            severity="secondary"
                            @click="manageBilling()"
                            label="View Invoices"
                            :loading="redirecting"
                            class="border-black"
                            size="small"
                        />

                        <span class="text-xs text-slate-400">
                            Redirects to a secure page hosted by Stripe
                        </span>
                    </div>

                    <Message severity="error" v-if="redirectError" class="mt-2">
                        {{ redirectError || 'An error occurred while redirecting to billing.' }}
                    </Message>
                </section>
            </div>
        </template>
    </Card>
</template>
